import { Component, inject, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AdyenCheckout, Card, CoreConfiguration, SubmitActions, SubmitData } from '@adyen/adyen-web';
import Order from '../../../smoothr-web-app-core/models/Order';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { AdyenCredentials } from '../../../smoothr-web-app-core/models/Venue';
import { PaySessionStatistics } from '../../../smoothr-web-app-core/models/PaySessionStatistics';
import { environment } from '../../../environments/environment';

export interface CreditCardModalResult {
	submitData: SubmitData;
	submitActions: SubmitActions;
}

@Component({
	standalone: false,
	selector: 'app-credit-card-modal',
	templateUrl: './credit-card-modal.component.html',
	styleUrls: ['./credit-card-modal.component.scss'],
})
export class CreditCardModalComponent implements OnInit {
	orders: Order[] = [];

	totalPrice = 0;
	formattedTotalPrice = '';
	formattedTips = '';
	paySessionStatistics: PaySessionStatistics | null = null;

	loading = true;

	private repositoryService = inject(RepositoryService);
	private adyenCredentials: AdyenCredentials | null = null;

	private modalCtrl: ModalController = inject(ModalController);
	private cardCheckout: Card | null = null;

	constructor() {}

	get isValid(): boolean {
		if (!this.cardCheckout) {
			return false;
		}
		return this.cardCheckout.isValid;
	}

	get formattedSelected() {
		return this.repositoryService.currencyFormat.format(this.paySessionStatistics?.selectedTotalPrice ?? 0);
	}

	static async show(
		modalCtrl: ModalController,
		paySessionStatistics: PaySessionStatistics | null,
		orders: Order[],
		adyenCredentials: AdyenCredentials
	): Promise<CreditCardModalResult> {
		const modal = await modalCtrl.create({
			component: CreditCardModalComponent,
			cssClass: 'payment-sheet-modal',
			componentProps: {
				orders,
				itemCount: orders.reduce((curr, acc) => {
					return curr + acc.checkoutOrderedArticles.length;
				}, 0),
				adyenCredentials,
				paySessionStatistics,
			},
		});

		await modal.present();
		const { data } = await modal.onDidDismiss();
		return data;
	}

	ngOnInit() {
		this.loadPayment();
	}

	async loadPayment() {
		await this.paymentMethodAdyen();
		this.loading = false;
	}

	async paymentMethodAdyen() {
		this.loading = true;
		const configuration: CoreConfiguration = {
			locale: 'de_DE',
			environment: environment.adyenEnvironment as 'test' | 'live' | 'live-us' | 'live-au' | 'live-apse' | 'live-in',
			clientKey: this.adyenCredentials?.clientKey,
			countryCode: 'DE',
			analytics: {
				enabled: false, //disable analytics cors errors
			},
		};
		const paymentMethods = await this.repositoryService.getAdyenPaymentMethods(this.orders[0].venue);
		console.log({
			adyenCredentials: this.adyenCredentials,
			paymentMethods,
		});
		const checkout = await AdyenCheckout(configuration);
		const modal = this;
		const setLoading = (loading: boolean) => {
			this.loading = loading;
		};
		const confirmAdyenForm = this.confirm;
		this.cardCheckout = new Card(checkout, {
			// @ts-ignore
			paymentMethodsResponse: paymentMethods,
			type: 'card',
			amount: {
				currency: this.orders[0].currency,
				value: (this.paySessionStatistics?.selectedTotalPrice ?? 0) * 100,
			},
			onLoad: function (value: any) {
				console.log('Custom Card Loaded', value);
				setLoading(false);
			},
			onError: function (err: any) {
				console.log('Error:', err);
				setLoading(false);
			},
			onChange: function (_: any) {},
			onSubmit: function (state, component, actions) {
				console.log('Custom Card Submitted', state, actions);
				confirmAdyenForm(modal, state, actions);
			},
			onAdditionalDetails: function (value: any) {
				console.log('Custom Card Additional Details', value);
			},
			showPayButton: false,
		}).mount('#customCard-container');
		this.reloadPrices();
	}

	async confirm(modal: CreditCardModalComponent, submitData: SubmitData, actions: SubmitActions) {
		if (!submitData || !submitData.isValid) {
			console.error('No Adyen data found or not valid', submitData);
			return;
		}
		// submitActions.resolve();
		await modal.modalCtrl.dismiss({
			submitData,
			submitActions: actions,
		});
	}

	async confirmButton() {
		const cardCheckout = this.cardCheckout;
		if (!cardCheckout) {
			console.error('No cardCheckout found');
			return;
		}
		if (!cardCheckout.isValid) {
			console.error('Card is not valid');
			return;
		}
		cardCheckout.submit();
	}

	reloadPrices() {
		const selectedTotalPrice = this.paySessionStatistics?.selectedTotalPrice ?? 0;
		const tipValue = this.paySessionStatistics?.tipsValue ?? 0;
		this.totalPrice = selectedTotalPrice;
		this.formattedTotalPrice = this.repositoryService.currencyFormat.format(this.totalPrice);
		this.formattedTips = this.repositoryService.currencyFormat.format(tipValue);
	}

	close() {
		this.modalCtrl.dismiss();
	}
}
